// src/components/shared/FieldModals.jsx
//
// Shared field-level modal components used platform-wide.
// When editing address, language, or other repeated field types,
// always import from here — changes propagate globally.

import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Bold, Italic, List, ListOrdered } from 'lucide-react';

// ─── Shared input styles ───────────────────────────────────

export const fieldModalInputStyle = {
  width: '100%', padding: '7px 10px', borderRadius: 6,
  border: '1px solid var(--border)', fontSize: 13,
  background: 'white', color: 'var(--text)', outline: 'none',
  boxSizing: 'border-box', fontFamily: 'inherit',
};

export const fieldModalLabelStyle = {
  display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--secondary)',
  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4,
};

const fieldWrap = { marginBottom: 14 };

// ─── SectionModal ─────────────────────────────────────────
// Generic wrapper for all field-edit popups.
// Props: title, onSave, onCancel, saving, children, wide (bool)

export const SectionModal = ({ title, onSave, onCancel, saving, disabled, children, wide }) =>
  ReactDOM.createPortal(
    <div className="modal-overlay" style={{ zIndex: 1000 }}>
      <div className="modal-box" style={{
        maxWidth: wide ? 700 : 560, width: '92vw',
        maxHeight: '80vh', display: 'flex', flexDirection: 'column',
      }}>
        <div className="modal-header">
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{title}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onCancel} style={{ padding: '4px 6px' }}>
            <X size={15} />
          </button>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto', flex: 1, padding: '20px 24px' }}>
          {children}
        </div>
        <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={onSave} disabled={saving || disabled}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );

// ─── Address ──────────────────────────────────────────────
// Helpers and form fields for structured address input.

export const EMPTY_ADDRESS = { street: '', suburb: '', state: '', postcode: '', country: '' };

export const formatAddress = (address) => {
  if (!address || typeof address !== 'object') return '';
  const { street, suburb, state, postcode, country } = address;
  const sp = [state, postcode].filter(Boolean).join(' ');
  return [street, suburb, sp, country].filter(Boolean).join(', ');
};

// AddressFields — renders all address inputs; parent owns the form state.
// Props: value (address object), onChange (fn receiving updated address object)
export const AddressFields = ({ value, onChange }) => {
  const set = (field) => (e) => onChange({ ...value, [field]: e.target.value });
  return (
    <>
      <div style={fieldWrap}>
        <label style={fieldModalLabelStyle}>Street</label>
        <input type="text" value={value.street} onChange={set('street')}
          style={fieldModalInputStyle} placeholder="123 Example Street" autoFocus />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
        <div style={fieldWrap}>
          <label style={fieldModalLabelStyle}>Suburb / City</label>
          <input type="text" value={value.suburb} onChange={set('suburb')}
            style={fieldModalInputStyle} placeholder="Suburb" />
        </div>
        <div style={fieldWrap}>
          <label style={fieldModalLabelStyle}>State / Region</label>
          <input type="text" value={value.state} onChange={set('state')}
            style={fieldModalInputStyle} placeholder="State" />
        </div>
        <div style={fieldWrap}>
          <label style={fieldModalLabelStyle}>Postcode</label>
          <input type="text" value={value.postcode} onChange={set('postcode')}
            style={fieldModalInputStyle} placeholder="Postcode" />
        </div>
        <div style={fieldWrap}>
          <label style={fieldModalLabelStyle}>Country</label>
          <input type="text" value={value.country} onChange={set('country')}
            style={fieldModalInputStyle} placeholder="Australia" />
        </div>
      </div>
    </>
  );
};

// AddressModal — complete self-contained address edit modal.
// Props: initialValue (address object), onSave (fn receiving address object), onCancel
export const AddressModal = ({ initialValue, onSave, onCancel }) => {
  const [form, setForm] = useState({ ...EMPTY_ADDRESS, ...initialValue });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(form); }
    finally { setSaving(false); }
  };

  return (
    <SectionModal title="Edit Address" onSave={handleSave} onCancel={onCancel} saving={saving}>
      <AddressFields value={form} onChange={setForm} />
    </SectionModal>
  );
};

// ─── Languages ────────────────────────────────────────────

export const LANGUAGES = [
  'English', 'Mandarin', 'Spanish', 'Hindi', 'Arabic', 'French', 'Bengali', 'Portuguese',
  'Russian', 'Urdu', 'Indonesian', 'German', 'Japanese', 'Swahili', 'Marathi', 'Telugu',
  'Turkish', 'Tamil', 'Vietnamese', 'Korean', 'Italian', 'Thai', 'Malay', 'Tagalog', 'Persian',
];

// LanguagePicker — tag-style multi-select with autocomplete dropdown.
// Props: selected (string[]), onChange (fn receiving string[])
export const LanguagePicker = ({ selected, onChange }) => {
  const [input, setInput] = useState('');
  const [showList, setShowList] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  const filtered = LANGUAGES.filter(
    (l) => l.toLowerCase().includes(input.toLowerCase()) && !selected.includes(l)
  ).slice(0, 8);

  const add = (lang) => {
    if (!selected.includes(lang)) onChange([...selected, lang]);
    setInput('');
    setShowList(false);
    inputRef.current?.focus();
  };

  const remove = (lang) => onChange(selected.filter((l) => l !== lang));

  const openDropdown = useCallback(() => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX, width: rect.width });
    }
    setShowList(true);
  }, []);

  return (
    <div>
      {selected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
          {selected.map((lang) => (
            <span key={lang} style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: '#EBF3FC', border: '1px solid #3D80CC', color: '#3D80CC',
              borderRadius: 12, padding: '2px 8px', fontSize: 12, fontWeight: 500,
            }}>
              {lang}
              <X size={10} style={{ cursor: 'pointer' }} onClick={() => remove(lang)} />
            </span>
          ))}
        </div>
      )}
      <div ref={wrapperRef}>
        <input
          ref={inputRef}
          type="text" value={input}
          onChange={(e) => { setInput(e.target.value); openDropdown(); }}
          onFocus={openDropdown}
          onBlur={() => setTimeout(() => setShowList(false), 150)}
          style={fieldModalInputStyle}
          placeholder={selected.length === 0 ? 'Type to add a language…' : 'Add another language…'}
        />
        {showList && filtered.length > 0 && ReactDOM.createPortal(
          <div style={{
            position: 'absolute',
            top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width,
            zIndex: 9999, background: 'white',
            border: '1px solid var(--border)', borderRadius: 6,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            maxHeight: 200, overflowY: 'auto',
          }}>
            {filtered.map((lang) => (
              <div key={lang} onMouseDown={() => add(lang)} style={{
                padding: '8px 12px', fontSize: 13, cursor: 'pointer', color: 'var(--text)',
              }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#F0F7FF'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
              >
                {lang}
              </div>
            ))}
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

// LanguagesModal — complete self-contained language edit modal.
// Props: initialValue (string[]), onSave (fn receiving string[]), onCancel
export const LanguagesModal = ({ initialValue, onSave, onCancel }) => {
  const [selected, setSelected] = useState([...initialValue]);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(selected); }
    finally { setSaving(false); }
  };

  return (
    <SectionModal title="Edit Working Languages" onSave={handleSave} onCancel={onCancel} saving={saving}>
      <div style={fieldWrap}>
        <label style={fieldModalLabelStyle}>Working Languages</label>
        <LanguagePicker selected={selected} onChange={setSelected} />
      </div>
    </SectionModal>
  );
};

// ─── RteModal ─────────────────────────────────────────────
// Rich-text edit modal (bold, italic, bullet list, numbered list).
// Props: title, initialHtml (string), onSave (fn receiving html string), onCancel, maxChars (optional)

export const RteModal = ({ title, initialHtml, maxChars, onSave, onCancel }) => {
  const editorRef               = useRef(null);
  const [charCount, setCharCount] = useState(0);
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.innerHTML = initialHtml || '';
    setCharCount((editor.innerText || '').length);
    editor.focus();
  }, []); // eslint-disable-line

  useEffect(() => {
    const editor = editorRef.current;
    let savedRange = null;

    const onSelChange = () => {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const r = sel.getRangeAt(0);
        if (editor && editor.contains(r.commonAncestorContainer)) {
          savedRange = r.cloneRange();
        }
      }
    };

    const onDocMousedown = (e) => {
      const btn = e.target.closest('[data-rte-cmd]');
      if (!btn) return;
      e.preventDefault();
      const ed = editorRef.current;
      if (ed) {
        ed.focus();
        if (savedRange) {
          try {
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(savedRange);
          } catch (_) {}
        }
      }
      const cmd = btn.getAttribute('data-rte-cmd');
      if (cmd === 'bold')   document.execCommand('bold',                false, null);
      if (cmd === 'italic') document.execCommand('italic',              false, null);
      if (cmd === 'ul')     document.execCommand('insertUnorderedList', false, null);
      if (cmd === 'ol')     document.execCommand('insertOrderedList',   false, null);
      const ed2 = editorRef.current;
      if (ed2) setCharCount((ed2.innerText || '').length);
    };

    document.addEventListener('selectionchange', onSelChange);
    document.addEventListener('mousedown', onDocMousedown);
    return () => {
      document.removeEventListener('selectionchange', onSelChange);
      document.removeEventListener('mousedown', onDocMousedown);
    };
  }, []); // eslint-disable-line

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(editorRef.current?.innerHTML || ''); } finally { setSaving(false); }
  };

  const tbStyle = {
    background: 'none', border: '1px solid #ddd', borderRadius: 3,
    padding: '4px 7px', cursor: 'pointer', display: 'flex', alignItems: 'center',
    color: '#333', lineHeight: 1,
  };
  const sep = <div style={{ width: 1, height: 20, background: '#ddd', margin: '0 2px' }} />;
  const overLimit = maxChars != null && charCount > maxChars;

  return ReactDOM.createPortal(
    <div className="modal-overlay" style={{ zIndex: 1100 }}>
      <div className="modal-box" style={{ maxWidth: 640, width: '94vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{title}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onCancel} style={{ padding: '4px 6px' }}>
            <X size={15} />
          </button>
        </div>
        <div className="modal-body" style={{ flex: 1, overflowY: 'auto', padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 5, alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: 10 }}>
            <button type="button" title="Bold"          data-rte-cmd="bold"   style={tbStyle}><Bold size={14} /></button>
            <button type="button" title="Italic"        data-rte-cmd="italic" style={tbStyle}><Italic size={14} /></button>
            {sep}
            <button type="button" title="Bullet list"   data-rte-cmd="ul"     style={tbStyle}><List size={14} /></button>
            <button type="button" title="Numbered list" data-rte-cmd="ol"     style={tbStyle}><ListOrdered size={14} /></button>
          </div>
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            className="rte-editor"
            onInput={() => setCharCount((editorRef.current?.innerText || '').length)}
            style={{
              flex: 1, minHeight: 220, border: '1px solid var(--border)', borderRadius: 4,
              padding: '10px 12px', fontSize: 14, lineHeight: 1.65,
              outline: 'none', background: 'white', overflowY: 'auto',
              fontFamily: 'inherit',
            }}
          />
          {maxChars != null && (
            <div style={{ fontSize: 11, textAlign: 'right', color: overLimit ? '#CC0000' : '#999' }}>
              {charCount} / {maxChars}
            </div>
          )}
        </div>
        <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || overLimit}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
